import { inject, Injectable } from '@angular/core';
import { ZENDESK_FIELDS, ZendeskOrigin } from '@app/shared/models/zendesk.model';
import { NativeService } from '@app/shared/services/native.service';
import { TokenService } from '@app/shared/services/token.service';
import { UserRepository } from '@app/shared/state/user/user.repository';
import { WalletRepository } from '@app/shared/state/wallet/wallet.repository';
import { User } from '@sentry/angular';

@Injectable({ providedIn: 'root' })
export class ZendeskFacade {
  private readonly nativeService = inject(NativeService);
  private readonly walletRepo = inject(WalletRepository);
  private readonly tokenService = inject(TokenService);
  private readonly userRepo = inject(UserRepository);

  get user(): User {
    return this.userRepo.user();
  }

  private readonly zendeskConfigs = {
    [ZendeskOrigin.InApp]: {
      formId: ZENDESK_FIELDS.formId,
      fields: (user: User) => ({
        [ZENDESK_FIELDS.username]: user.userName,
        [ZENDESK_FIELDS.usernameHidden]: user.userName,
        [ZENDESK_FIELDS.userId]: `${user.id}`,
        [ZENDESK_FIELDS.userIdHidden]: `${user.id}`,
        [ZENDESK_FIELDS.shopLevel]: `${this.tokenService.getCurrentInfo()?.shopLevel}`,
        [ZENDESK_FIELDS.buys]: `${user.buys}`,
        [ZENDESK_FIELDS.email]: user.email,
        [ZENDESK_FIELDS.date]: new Date().toISOString(),
        [ZENDESK_FIELDS.logged]: 'true',
        [ZENDESK_FIELDS.loading]: 'false',
        [ZENDESK_FIELDS.wallet]: this.walletRepo.getWalletToString(),
        [ZENDESK_FIELDS.deviceType]: this.getDeviceType(),
      }),
    },

    [ZendeskOrigin.Loading]: {
      formId: ZENDESK_FIELDS.formId,
      fields: (user: User) => ({
        [ZENDESK_FIELDS.username]: user.userName,
        [ZENDESK_FIELDS.usernameHidden]: user.userName,
        [ZENDESK_FIELDS.userId]: `${user.id}`,
        [ZENDESK_FIELDS.userIdHidden]: `${user.id}`,
        [ZENDESK_FIELDS.shopLevel]: `${this.tokenService.getCurrentInfo()?.shopLevel}`,
        [ZENDESK_FIELDS.buys]: `${user.buys}`,
        [ZENDESK_FIELDS.email]: user.email,
        [ZENDESK_FIELDS.date]: new Date().toISOString(),
        [ZENDESK_FIELDS.logged]: 'true',
        [ZENDESK_FIELDS.loading]: 'true',
        [ZENDESK_FIELDS.wallet]: this.walletRepo.getWalletToString(),
        [ZENDESK_FIELDS.deviceType]: this.getDeviceType(),
      }),
    },

    [ZendeskOrigin.Login]: {
      formId: ZENDESK_FIELDS.formId,
      fields: (user: User) => ({
        [ZENDESK_FIELDS.username]: user.userName,
        [ZENDESK_FIELDS.deviceType]: this.getDeviceType(),
        [ZENDESK_FIELDS.logged]: 'false',
        [ZENDESK_FIELDS.loading]: 'false',
        [ZENDESK_FIELDS.date]: new Date().toISOString(),
      }),
    },
  };

  getDeviceType(): string {
    switch (this.nativeService.nativeInfo?.deviceType) {
      case 'android':
        return 'android';

      case 'ios':
        return 'ios_apple_';

      default:
        return 'pc';
    }
  }

  buildZendeskUrl(formId: number, fields: Record<string, string>): string {
    const params = new URLSearchParams({
      ticket_form_id: formId.toString(),
      p: 'cD1XZWI=',
    });

    Object.entries(fields).forEach(([id, value]) => {
      if (value) {
        params.append(`tf_${id}`, value);
      }
    });

    return `https://support.slot.com/hc/es/requests/new?${params}`;
  }

  goToContact(state: ZendeskOrigin): void {
    window.open(
      this.buildZendeskUrl(this.zendeskConfigs[state].formId, this.zendeskConfigs[state].fields(this.user)),
      '_blank'
    );
  }
}
